/**
 * USE MUESTRAS - Hooks para el módulo de toma de muestras
 *
 * Flujo: Cola → Abrir atención → Encuesta → Validar → Generar muestras → Toma → Etiqueta → Cerrar
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import {
  abrirAtencion,
  cerrarAtencion,
  createTipoContenedor,
  deactivateTipoContenedor,
  deleteTipoContenedor,
  generarMuestras,
  getAtencionDetalle,
  getColaAtencion,
  getEtiqueta,
  getEtiquetasAtencion,
  getPerfilClinicoPrevio,
  getTiposContenedor,
  marcarInsuficiente,
  overrideManual,
  rechazarMuestra,
  registrarEncuesta,
  registrarToma,
  reimprimirEtiqueta,
  repetirMuestra,
  updateTipoContenedor,
  validarExamenes,
  verificarCompletitud,
  type AbrirAtencionData,
  type CerrarAtencionData,
  type EncuestaData,
  type OverrideManualData,
  type RechazarMuestraData,
  type RegistrarTomaData,
  type ReimprimirEtiquetaData,
  type TipoContenedorCreateData,
  type TipoServicio,
} from "@/services/muestras";

// Re-export types for pages
export type {
  Atencion,
  AtencionDetalle,
  AbrirAtencionData,
  CerrarAtencionData,
  ClasificacionAptitud,
  ColaAtencionItem,
  CondicionActual,
  CondicionActualData,
  EncuestaData,
  EstadoAtencion,
  EstadoMuestra,
  Incidencia,
  MotivoRechazo,
  Muestra,
  MuestraExamen,
  MuestraListItem,
  OverrideManualData,
  PerfilClinicoBase,
  PerfilClinicoBaseData,
  RechazarMuestraData,
  RechazoMuestra,
  RegistrarTomaData,
  ReimprimirEtiquetaData,
  TipoContenedor,
  TipoContenedorCreateData,
  TipoIncidencia,
  TipoMuestraBiologica,
  TipoServicio,
  ValidacionPreanalitica,
  VerificacionCompletitud,
} from "@/services/muestras";

// Re-export fetch functions for useServerDataTable
export { getTiposContenedor } from "@/services/muestras";

interface MutationCallbacks<T = void> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

// =============================================================================
// QUERIES — Cola de Atención
// =============================================================================

export function useColaAtencion(
  sucursalId: number,
  tipoServicio?: TipoServicio,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.muestras.cola(sucursalId, tipoServicio),
    queryFn: () => getColaAtencion({ sucursal: sucursalId, tipo_servicio: tipoServicio }),
    enabled: (options?.enabled ?? true) && sucursalId > 0,
    staleTime: 0, // siempre considerar stale para que el polling funcione
    refetchInterval: 15 * 1000, // polling cada 15s — órdenes nuevas o atendidas por otros
  });
}

// =============================================================================
// QUERIES — Atención
// =============================================================================

export function useAtencionDetalle(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.muestras.detail(id),
    queryFn: () => getAtencionDetalle(id),
    enabled: (options?.enabled ?? true) && id > 0,
    staleTime: 0,
    // Polling cada 10s para sincronizar entre flebotomistas.
    // Se pausa automáticamente cuando la pestaña no está visible.
    // Se detiene cuando la atención llega a estado terminal (no tiene sentido seguir).
    refetchInterval: (query) => {
      const estado = (query.state.data as import("@/services/muestras").AtencionDetalle | undefined)
        ?.estado;
      if (estado === "CERRADA" || estado === "CERRADA_PARCIAL" || estado === "CANCELADA") {
        return false;
      }
      return 10 * 1000;
    },
  });
}

export function usePerfilClinicoPrevio(atencionId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.muestras.perfilClinico(atencionId),
    queryFn: () => getPerfilClinicoPrevio(atencionId),
    enabled: (options?.enabled ?? true) && atencionId > 0,
  });
}

export function useVerificarCompletitud(atencionId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.muestras.completitud(atencionId),
    queryFn: () => verificarCompletitud(atencionId),
    enabled: (options?.enabled ?? true) && atencionId > 0,
    staleTime: 0,
    refetchInterval: 10 * 1000,
  });
}

// =============================================================================
// QUERIES — Tipos de Contenedor
// =============================================================================

export function useTiposContenedor() {
  return useQuery({
    queryKey: queryKeys.muestras.tiposContenedor,
    queryFn: () => getTiposContenedor(),
    staleTime: 10 * 60 * 1000, // 10 min — catálogo estable
  });
}

// =============================================================================
// MUTATIONS — Abrir Atención
// =============================================================================

export function useAbrirAtencion(
  callbacks?: MutationCallbacks<import("@/services/muestras").Atencion>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AbrirAtencionData) => abrirAtencion(data),
    onSuccess: (atencion) => {
      // Forzar refetch inmediato de la cola — la orden debe cambiar a "Continuar atención"
      queryClient.refetchQueries({ queryKey: ["muestras", "cola"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.all });
      callbacks?.onSuccess?.(atencion);
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// MUTATIONS — Encuesta Preanalítica
// =============================================================================

export function useRegistrarEncuesta(atencionId: number, callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EncuestaData) => registrarEncuesta(atencionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.detail(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.perfilClinico(atencionId) });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// MUTATIONS — Validación Preanalítica
// =============================================================================

export function useValidarExamenes(atencionId: number, callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => validarExamenes(atencionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.detail(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.validaciones(atencionId) });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useOverrideManual(
  atencionId: number,
  ordenExamenId: number,
  callbacks?: MutationCallbacks
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OverrideManualData) => overrideManual(atencionId, ordenExamenId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.detail(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.validaciones(atencionId) });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// MUTATIONS — Generar Muestras
// =============================================================================

export function useGenerarMuestras(atencionId: number, callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => generarMuestras(atencionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.detail(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.muestras(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.completitud(atencionId) });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// MUTATIONS — Toma de Muestras
// =============================================================================

export function useRegistrarToma(atencionId: number, callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ muestraId, data }: { muestraId: number; data: RegistrarTomaData }) =>
      registrarToma(muestraId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.detail(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.muestras(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.completitud(atencionId) });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useRechazarMuestra(atencionId: number, callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ muestraId, data }: { muestraId: number; data: RechazarMuestraData }) =>
      rechazarMuestra(muestraId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.detail(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.muestras(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.completitud(atencionId) });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useMarcarInsuficiente(atencionId: number, callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (muestraId: number) => marcarInsuficiente(muestraId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.detail(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.muestras(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.completitud(atencionId) });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useRepetirMuestra(atencionId: number, callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (muestraId: number) => repetirMuestra(muestraId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.detail(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.muestras(atencionId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.completitud(atencionId) });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// MUTATIONS — Etiquetas (retornan Blob para descarga)
// =============================================================================

export function useGetEtiqueta(callbacks?: MutationCallbacks<Blob>) {
  return useMutation({
    mutationFn: (muestraId: number) => getEtiqueta(muestraId),
    onSuccess: (blob) => callbacks?.onSuccess?.(blob),
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useGetEtiquetasAtencion(callbacks?: MutationCallbacks<Blob>) {
  return useMutation({
    mutationFn: (atencionId: number) => getEtiquetasAtencion(atencionId),
    onSuccess: (blob) => callbacks?.onSuccess?.(blob),
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useReimprimirEtiqueta(muestraId: number, callbacks?: MutationCallbacks<Blob>) {
  return useMutation({
    mutationFn: (data: ReimprimirEtiquetaData) => reimprimirEtiqueta(muestraId, data),
    onSuccess: (blob) => callbacks?.onSuccess?.(blob),
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// MUTATIONS — Cerrar Atención
// =============================================================================

export function useCerrarAtencion(
  atencionId: number,
  callbacks?: MutationCallbacks<import("@/services/muestras").Atencion>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CerrarAtencionData) => cerrarAtencion(atencionId, data),
    onSuccess: (atencion) => {
      // Forzar refetch inmediato de la cola — invalidateQueries no es suficiente
      // porque refetchOnMount: false evita el refetch al navegar de vuelta
      queryClient.refetchQueries({ queryKey: ["muestras", "cola"] });
      // Invalidar el resto del módulo
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.all });
      callbacks?.onSuccess?.(atencion);
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// MUTATIONS — TipoContenedor CRUD
// =============================================================================

export function useCreateTipoContenedor(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TipoContenedorCreateData) => createTipoContenedor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.tiposContenedor });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useUpdateTipoContenedor(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TipoContenedorCreateData> }) =>
      updateTipoContenedor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.tiposContenedor });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeactivateTipoContenedor(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deactivateTipoContenedor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.tiposContenedor });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeleteTipoContenedor(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteTipoContenedor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.muestras.tiposContenedor });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

// =============================================================================
// QUERIES — Estadísticas (M4.10)
// =============================================================================

export function useEstadisticasMuestras(
  params: import("@/services/muestras").EstadisticasParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.muestras.estadisticas(params),
    queryFn: () => import("@/services/muestras").then((m) => m.getEstadisticasMuestras(params)),
    enabled: options?.enabled ?? true,
    staleTime: 60 * 1000, // 1 min — estadísticas no necesitan polling
  });
}
